import api from 'services/api';
import { REJECT, array, nullable, num, numeric, object, optional, str, unknownRecord, unknownValue, type Received } from 'validation';

import { readResponse } from 'api/shared/readResponse';

// Source of truth for the wire shape: nap-backend `routes/workflows.js` GET /workflows, one page of the inbox. Each row
// also carries the workflow's parameters, stages, data and `can_confirm`, and each action its message, inputs, files and
// dropdown parameters; the sidebar reads none of those, and the detail panel reads its workflow by id
// (`getInboxWorkflow`). This projection declares what the sidebar renders: the subject, date and zone of each row, and
// per action only whether it is still open. The zone is null for a message without one (nap-backend#2011).
//
// `zoneOptions` are the zones the caller can filter on: where they hold workflows.listAll or workflows.confirm, every
// zone for a superadmin. The `zone` parameter filters server-side, so the page totals count the filtered set.

export interface InboxListAction {
  readonly workflow_action_id: number;
  /** Only the length is read: an action with options and no response is still open. */
  readonly response_options: readonly unknown[];
  readonly response: Readonly<Record<string, unknown>> | null;
}

export interface InboxListWorkflow {
  readonly workflow_id: number;
  readonly workflow_subject: string | null;
  readonly created_at: string;
  /** Display only, so optional: a backend without it (before nap-backend#2011) still lists. */
  readonly zone_name?: string | null;
  readonly workflows_actions: readonly InboxListAction[];
}

export interface InboxListPagination {
  readonly total_records: number;
  readonly total_pages: number;
  readonly current_page: number;
  readonly limit: number;
  readonly offset: number;
}

export interface InboxZoneOption {
  readonly zone_id: number;
  readonly zone_name: string;
}

export interface InboxWorkflowsPage {
  readonly workflows: readonly InboxListWorkflow[];
  readonly filterOptions: readonly string[];
  readonly zoneOptions: readonly InboxZoneOption[];
  readonly pagination: InboxListPagination;
}

export interface InboxWorkflowsParams {
  readonly filter: string;
  readonly page: number;
  readonly limit: number;
  readonly sortOrder: 'asc' | 'desc';
  /** Zone names; none means every zone. */
  readonly zone?: readonly string[];
}

const actionSchema = object<InboxListAction>({
  workflow_action_id: num(REJECT),
  response_options: array(unknownValue()),
  response: nullable(unknownRecord()),
});

const workflowSchema = object<InboxListWorkflow>({
  workflow_id: num(REJECT),
  workflow_subject: nullable(str()),
  created_at: str(),
  zone_name: optional(nullable(str())),
  workflows_actions: array(actionSchema),
});

// COUNT-derived totals can arrive as numeric strings from Postgres; `numeric()` normalizes both number and string.
const paginationSchema = object<InboxListPagination>({
  total_records: numeric(),
  total_pages: numeric(),
  current_page: numeric(),
  limit: numeric(),
  offset: numeric(),
});

// A zone without its name cannot be offered: the filter sends names.
const zoneOptionSchema = object<InboxZoneOption>({ zone_id: num(REJECT), zone_name: str(REJECT) });

const pageSchema = object<InboxWorkflowsPage>({
  workflows: array(workflowSchema, REJECT),
  filterOptions: array(str()),
  zoneOptions: array(zoneOptionSchema),
  pagination: paginationSchema,
});

/** GET `/workflows`: one page of the inbox, as the sidebar shows it. */
export const getInboxWorkflows = async (params: InboxWorkflowsParams): Promise<Received<InboxWorkflowsPage>> => {
  // `zone=A&zone=B`, as the route documents it, rather than axios's default `zone[]=A&zone[]=B`.
  const res = await api.get('/workflows', { params, paramsSerializer: { indexes: null } });
  return readResponse(res.data, { schema: pageSchema, source: 'getInboxWorkflows' });
};
